import React from "react";
import { Modal, ModalHeader, ModalBody, Row, Col } from "reactstrap";
import styles from "../css/verificationQueue.module.scss";
import { formatDate } from "common/service/CommonService";
import { MODULE_UI_CONFIGS, getRecordSections, getBadgeConfig } from "../helper/verification.constants";
import { DetailField, DetailSection } from "../helper/verification.types";

// ==========================================
// INTERFACES & CONFIG TYPES
// ==========================================
// Definitions imported from ../helper/verification.types

interface VerificationDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  record: any;
}

// Definitions extracted to src/modules/Verification/helper/verification.constants.ts

const VerificationDetailModal: React.FC<VerificationDetailModalProps> = ({
  isOpen,
  toggle,
  record,
}) => {
  if (!record) return null;

  // Badge config helper (imported from verification.constants.ts)

  // Prepare dynamic configuration layout sections (imported from verification.constants.ts)

  const badge = getBadgeConfig(record.moduletype);
  const sections = getRecordSections(record);
  const volunteerInfoSection = sections[0];
  const detailSection = sections[1];

  const config = MODULE_UI_CONFIGS[record.moduletype] || {
    label: record.moduletype,
    badgeIcon: "ri-notification-3-line",
    heroIcon: "ri-notification-3-line",
    badgeClass: "",
    heroClass: "",
    heroTitlePrefix: "Request",
  };

  const isIpModule = [
    "IP_INVENTORY",
    "RETENTION",
    "DISPENSING",
    "RETURNS_LOG",
    "DRUG_TRANSFER",
    "DISPOSAL",
    "RECONCILIATION"
  ].includes(record.moduletype);

  let heroTitleName = "-";
  if (isIpModule) {
    heroTitleName = record.recorddetail?.productname || "-";
  } else if (record.moduletype === "ADVERSE_EVENT") {
    heroTitleName = record.recorddetail?.eventterm || "-";
  } else if (record.moduletype === "AMBULATORY_VISIT") {
    heroTitleName = record.recorddetail?.visitname || record.recorddetail?.visitnumber || "-";
  } else if (record.moduletype === "LAB_TEST") {
    heroTitleName = record.recorddetail?.reportname || "-";
  } else {
    heroTitleName = record.recorddetail?.initials || "-";
  }

  const heroTitle = `${config.heroTitlePrefix}: ${heroTitleName}`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" returnFocusAfterClose={false}>
      {/* HEADER */}
      <ModalHeader toggle={toggle} className="border-bottom p-3">
        <span className="fw-semibold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
          <i className="ri-information-line text-primary" />
          Message Details
        </span>
      </ModalHeader>

      {/* BODY */}
      <ModalBody className="p-0">
        <div className={styles.detailBody}>
          {/* Hero Card */}
          <div className={`${styles.heroCard} ${config.heroClass}`}>
            <div className={styles.heroAvatar}>
              <i className={config.heroIcon} />
            </div>
            <div className={styles.heroInfo}>
              <div className={styles.heroTitleRow}>
                <h4 className={styles.heroTitle}>
                  {heroTitle}
                </h4>
                <span className={styles.heroSubtitlePill}>
                  {record.moduletype === "TRANSFER"
                    ? `${record.recorddetail.fromstudycode || "-"} ➔ ${record.recorddetail.tostudycode || "-"}`
                    : record.recorddetail.studycode
                  }
                </span>
              </div>
              <div className={styles.heroMetaRow}>
                <span className={styles.heroDot}>•</span>
                <span className={styles.heroMetaType}>{badge.label}</span>
              </div>
            </div>
          </div>

          {/* Sections Row */}
          <Row className="g-3">
            {/* LEFT COLUMN: Volunteer Info & Remarks */}
            <Col md={6} className="d-flex flex-column gap-2">
              {volunteerInfoSection && (
                <div className={`${styles.sectionBox} mb-0`}>
                  <h4>
                    <i className={volunteerInfoSection.icon} /> {volunteerInfoSection.title}
                  </h4>
                  <div className={styles.infoGrid}>
                    {volunteerInfoSection.fields.map((field, fIdx) => (
                      <div
                        key={fIdx}
                        className={`${styles.infoItem} ${field.fullWidth ? styles.fullWidth : ""}`}
                      >
                        <span className={styles.infoLabel}>{field.label}</span>
                        <span className={`${styles.infoValue} ${field.highlightClass || ""}`}>
                          {field.value || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Remarks Box */}
              <div className={`${styles.sectionBox} flex-grow-1 mb-0`}>
                <h4>
                  <i className="ri-chat-4-line" /> Remarks / Notes
                </h4>
                <div className={styles.remarksBubble}>
                  {record.recorddetail.remarks || record.recorddetail.comments || "No additional remarks provided."}
                </div>
              </div>
            </Col>

            {/* RIGHT COLUMN: Block/Dropout Details */}
            <Col md={6}>
              {detailSection && (
                <div className={`${styles.sectionBox} h-100 mb-0`}>
                  <h4>
                    <i className={detailSection.icon} /> {detailSection.title}
                  </h4>
                  <div className={styles.infoGrid}>
                    {detailSection.fields.map((field, fIdx) => (
                      <div
                        key={fIdx}
                        className={`${styles.infoItem} ${field.fullWidth ? styles.fullWidth : ""}`}
                      >
                        <span className={styles.infoLabel}>{field.label}</span>
                        <span className={`${styles.infoValue} ${field.highlightClass || ""}`}>
                          {field.value || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default VerificationDetailModal;
